import dayjs from "dayjs"
import { formatDurationFields } from "utils/experience"
import { getMultiDaySpan, getSlotStartLabel, getTicketedSlotEndLabel } from "utils/experienceAvailability"
import { GUEST_TYPES } from "./WidgetExperience/constants"
import { getBookableTicketTypes } from "./helpers"

// Text describing one booking selection, shared by the cart and checkout.

// Single day: "Mon, June 9, 2026 · 08:00 AM (2 hrs)" (a ticketed booking: "· 04:00 PM (2 hours)").
// Multi-day: "Thu, Aug 19 – Sat, Aug 21, 2026 · 3-day booking · Starts at 08:00 AM"; the end date is
// always start date + span (multi-day bookings have no stored end date).
export const describeBookingDate = ({ experience, selection, t }) => {
  const start = selection?.date ? dayjs(selection.date) : null
  if (!start) return ""

  if (experience?.duration_type === "multi_day") {
    const spanDays = getMultiDaySpan(experience)
    const end = start.add(spanDays - 1, "day")
    const startFormat = start.year() === end.year() ? "ddd, MMM D" : "ddd, MMM D, YYYY"
    const parts = [
      `${start.format(startFormat)} – ${end.format("ddd, MMM D, YYYY")}`,
      t("booking_widget.booking.multi_day_length", { count: spanDays })
    ]
    if (selection?.timeSlot) parts.push(t("booking_widget.booking.starts_at", { time: getSlotStartLabel(selection.timeSlot) }))
    return parts.join(" · ")
  }

  const parts = [start.format("ddd, MMMM D, YYYY")]
  if (selection?.timeSlot) {
    const duration = selection?.tripLength?.value || (isTicketedSelection(selection) ? formatDurationFields(experience, t) : null)
    parts.push(duration ? `${getSlotStartLabel(selection.timeSlot)} (${duration})` : getSlotStartLabel(selection.timeSlot))
  }
  return parts.join(" · ")
}

// "4 adults, 1 child"
export const describeGuests = ({ counts, t }) =>
  GUEST_TYPES.map(({ key }) => ({ key, count: Number(counts?.main?.[key] || 0) }))
    .filter(({ count }) => count > 0)
    .map(({ key, count }) => t(`booking_widget.booking.guests.${key}`, { count }))
    .join(", ")

// "3 Adults, 1 VIP": the tickets of a ticketed booking, in ticket type order.
export const describeTickets = ({ experience, ticketCounts, t }) =>
  getBookableTicketTypes(experience)
    .map((ticketType) => ({ title: ticketType.title, count: Number(ticketCounts?.[ticketType.id] || 0) }))
    .filter(({ count }) => count > 0)
    .map(({ title, count }) => t("booking_widget.booking.ticket_count", { count, title }))
    .join(", ")

// A ticketed booking holds a scheduled start time and ticket counts instead of a duration and
// guest counts.
export const isTicketedSelection = (item) => !!item?.scheduledSlot

// Whether the cart holds a booking (added with Add to Cart or Book Now; choices still being made
// on an experience page live in a separate draft and don't count).
export const hasCartItem = (item) => !!(item?.experienceId && item?.date && (item?.tripLength || isTicketedSelection(item)))

// Checkout's two-line "Date and time":
// single day  -> ["Mon, June 9, 2026", "08:00 AM - 10:00 AM (2 hrs)"] (ticketed: the end is the
//                start plus the trip's length, "04:00 PM - 06:00 PM (2 hours)")
// multi-day   -> ["Thu, Aug 19 – Sat, Aug 21, 2026", "3-day booking", "Starts at 08:00 AM"]
export const describeBookingDateLines = ({ experience, selection, t }) => {
  const start = selection?.date ? dayjs(selection.date) : null
  if (!start) return []
  if (experience?.duration_type === "multi_day") {
    return describeBookingDate({ experience, selection, t }).split(" · ")
  }
  const slot = selection?.timeSlot
  if (!slot) return [start.format("ddd, MMMM D, YYYY")]
  if (isTicketedSelection(selection)) {
    const endLabel = getTicketedSlotEndLabel(experience, selection.scheduledSlot.startsAt)
    const range = endLabel ? `${slot} - ${endLabel}` : slot
    const duration = formatDurationFields(experience, t)
    return [start.format("ddd, MMMM D, YYYY"), duration ? `${range} (${duration})` : range]
  }
  const range = String(slot)
    .split(" - ")
    .map((part) => getSlotStartLabel(part))
    .join(" - ")
  const duration = selection?.tripLength?.value
  return [start.format("ddd, MMMM D, YYYY"), duration ? `${range} (${duration})` : range]
}
